'use client';

import { c as _c } from "react/compiler-runtime";
import SvgAdd from '@common/icon/SvgAdd';
import { cn } from '@core/utils';
import { useLinkStatus } from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { ServiceSwitchDropdown } from '@/app/_components/ServiceSwitchDropdown';
import { DONATION_BNB_TOAST_ITEMS } from '@/app/donation/constants';
import { DONATION_ROUTES, SUB_DOMAIN } from '@/constants';
import { useHaptic } from '@/hooks/useHaptic';
import { usePartnerChromeClassName } from '@/hooks/usePartnerChromeClassName';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { scrollToPageTop } from '@/utils';
import { gtmDatasets } from '@/utils/analytics/gtm';
type DonationBnbLinkContentProps = Pick<(typeof DONATION_BNB_TOAST_ITEMS)[number], 'label' | 'icon'> & {
  isActive: boolean;
};
const DonationBnbLinkContent = t0 => {
  const $ = _c(24);
  const {
    label,
    icon: Icon,
    isActive
  } = t0;
  const {
    pending
  } = useLinkStatus();
  const iconContainerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== pending) {
    t1 = () => {
      if (!pending) {
        return;
      }
      const linkElement = iconContainerRef.current?.closest("a");
      if (!linkElement) {
        return;
      }
      const preventRepeatedNavigation = _temp;
      linkElement.setAttribute("aria-busy", "true");
      linkElement.setAttribute("aria-disabled", "true");
      linkElement.addEventListener("click", preventRepeatedNavigation, true);
      return () => {
        linkElement.removeAttribute("aria-busy");
        linkElement.removeAttribute("aria-disabled");
        linkElement.removeEventListener("click", preventRepeatedNavigation, true);
      };
    };
    t2 = [pending];
    $[0] = pending;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== pending) {
    t3 = cn("transition-opacity", {
      "animate-pulse opacity-60 motion-reduce:animate-none": pending
    });
    $[3] = pending;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = isActive || pending;
  let t5;
  if ($[5] !== t4) {
    t5 = cn("size-28 text-Gray-400", {
      "text-Gray-black": t4
    });
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== Icon || $[8] !== t5) {
    t6 = <Icon className={t5} />;
    $[7] = Icon;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <div ref={iconContainerRef} className={t3}>{t6}</div>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = isActive || pending;
  let t9;
  if ($[13] !== t8) {
    t9 = cn("whitespace-nowrap text-center font-400 text-Gray-550 transition-colors typo-caption2", {
      "font-600 text-Gray-900": t8
    });
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== label || $[16] !== t9) {
    t10 = <p className={t9}>{label}</p>;
    $[15] = label;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = pending ? "\uD398\uC774\uC9C0 \uC774\uB3D9 \uC911" : null;
  let t12;
  if ($[18] !== t11) {
    t12 = <span aria-live="polite" className="sr-only" role="status">{t11}</span>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t7) {
    t13 = <>{t7}{t10}{t12}</>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t7;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
};
export const DonationBnb = () => {
  const $ = _c(14);
  const partnerChromeClassName = usePartnerChromeClassName();
  const pathname = usePathname();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const canSwitchService = useDeviceInfoStore(_temp2);
  const {
    triggerHaptic
  } = useHaptic();
  let t0;
  if ($[0] !== partnerChromeClassName) {
    t0 = cn(partnerChromeClassName, "relative flex size-full items-center gap-12 p-8");
    $[0] = partnerChromeClassName;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== canSwitchService || $[3] !== isDropdownOpen) {
    t1 = canSwitchService ? <div className="relative z-50"><button type="button" className="relative z-50 size-44 rounded-pill bg-Gray-100 transition-opacity duration-200 ease-out flex-center hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 active:opacity-80" onClick={() => setIsDropdownOpen(_temp3)} aria-label={"\uB2E4\uB978 \uC11C\uBE44\uC2A4\uB85C \uC774\uB3D9"} aria-expanded={isDropdownOpen}><SvgAdd className={cn("size-20 text-Gray-600 transition-transform duration-200", {
          "rotate-45": isDropdownOpen
        })} /></button><ServiceSwitchDropdown isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)} /></div> : null;
    $[2] = canSwitchService;
    $[3] = isDropdownOpen;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== pathname || $[6] !== triggerHaptic) {
    t2 = DONATION_BNB_TOAST_ITEMS.map(t3 => {
      const {
        label,
        route,
        icon: Icon
      } = t3;
      const isActive = (() => {
        if (!pathname) {
          return false;
        }
        if (route.pathname === DONATION_ROUTES.HOME.pathname) {
          return pathname === route.pathname;
        }
        return pathname.includes(route.pathname);
      })();
      const isCurrentPage = pathname === route.pathname;
      const clickFnbDataset = gtmDatasets.clickFnb({
        menu_name: label
      });
      return <li key={label} className="min-w-0 flex-1"><CustomLink href={route.pathname} aria-current={isCurrentPage ? "page" : undefined} aria-label={`${label} 페이지로 이동`} prefetch={true} className="flex w-full flex-col items-center" onClick={event => {
          if (isCurrentPage) {
            event.preventDefault();
            scrollToPageTop();
            return;
          }
          triggerHaptic();
        }} {...clickFnbDataset}><DonationBnbLinkContent icon={Icon} isActive={isActive} label={label} /></CustomLink></li>;
    });
    $[5] = pathname;
    $[6] = triggerHaptic;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <ul className="flex flex-1 items-center">{t2}</ul>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t0 || $[11] !== t1 || $[12] !== t3) {
    t4 = <nav aria-label={"\uAE30\uBD80 \uB0B4\uBE44\uAC8C\uC774\uC158"} className={t0}>{t1}{t3}</nav>;
    $[10] = t0;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};
function _temp(event) {
  event.preventDefault();
  event.stopPropagation();
}
function _temp2(state) {
  return state.subDomain !== SUB_DOMAIN.TOSS && state.subDomain !== SUB_DOMAIN.KAKAOBANK;
}
function _temp3(prev) {
  return !prev;
}